import React from "react";
import currency from "currency.js";
import { Dropdown } from "react-bootstrap";
import { FieldArrayWithId, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { FormCheck, FormInput, registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as EditSVG } from "go-core/images/svg/actions/edit.svg";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as LongArrowDownSVG } from "../../../../../../../../../../../../images/svg/documents/long-arrow-down.svg";
import { ReactComponent as LongArrowUpSVG } from "../../../../../../../../../../../../images/svg/documents/long-arrow-up.svg";
import { ReactComponent as MoreSVG } from "../../../../../../../../../../../../images/svg/documents/more.svg";
import {
	ProductApi,
	ProductStateApi,
	PurchaseApi,
	PurchaseItemApi,
	TaxApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../../services/Api/api";
import DocumentItemPriceDifferential from "../../../../../../components/DocumentItemPriceDifferential";
import { getProductVolumeLabel, isItemRowIndexShown, showMore } from "../../../../../../utils/itemsTableUtils";
import {
	drawProductState,
	getInStockAmountBadgeColor,
	getStateForProduct,
} from "../../../../../../utils/productStateUtils";

interface Props {
	watchedItems: PurchaseItemApi[];
	fields: FieldArrayWithId<PurchaseApi, "items", "key">[];
	shownIndexes: number[];
	form: UseFormReturn<PurchaseApi>;
	setShownIndexes: (newIndexes: number[]) => void;
	onChangeProduct: (product: ProductApi, index: number, createdProduct?: { label: string; value: string }) => void;
	onParseOptionValue: (option: { label: string }) => string;
	searchProducts: (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any> | undefined,
		index?: number
	) => Promise<ProductApi[]>;
	productStates: ProductStateApi[];
	setShowChangeProductModal: (value: number | null) => void;
	showDeviationFromReferencePrice: boolean;
	remove: UseFieldArrayRemove;
	indexToFocus: number;
	setIndexToFocus: (value: number) => void;
	fetchProductLastPrice: (item: PurchaseItemApi, index: number) => void;
	recalculateSummaryTaxes: (
		items: PurchaseItemApi[],
		taxGross: boolean,
		index?: number,
		val?: number,
		quantity?: string,
		isTotalPrice?: boolean
	) => void;
	fetchPurchase?: () => void;
	externalProductStates: ProductStateApi[];
	getExternalStateName: () => string;
}

const PurchaseFormItemsTableBody = ({
	watchedItems,
	fields,
	shownIndexes,
	form,
	setShownIndexes,
	onChangeProduct,
	onParseOptionValue,
	searchProducts,
	productStates,
	setShowChangeProductModal,
	showDeviationFromReferencePrice,
	remove,
	indexToFocus,
	setIndexToFocus,
	fetchProductLastPrice,
	recalculateSummaryTaxes,
	fetchPurchase,
	externalProductStates,
	getExternalStateName,
}: Props) => {
	const { t } = useTranslation();
	const {
		register,
		formState: { errors },
		control,
		setValue,
		watch,
		getValues,
	} = form;
	const organization = useSelector(selectOrganization);
	const watchedCurrency = watch("currency");
	const INPUT_AMOUNT_USING_TAB_KEY = 4;
	const PRODUCT_INPUT_INDEX = 1;
	const QUANTITY_INPUT_INDEX = 3;
	const PRICE_NET_INPUT_INDEX = 4;
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();

	const onRemove = (index: number) => {
		remove(index);
	};

	const getTotalVolume = (item: PurchaseItemApi) => {
		const unit = item?.product?.measure_type;
		const amount = ((item.volume || 0) * (item.quantity || 0)).toFixed(5);
		return `${amount} ${unit ? t(`enums.products.measure_types.${item?.product?.measure_type}`) : ""}`;
	};

	const drawPriceDeviation = (item: PurchaseItemApi) => {
		if ((item?.price_net_money?.amount || 0) > (item?.last_price || 0)) return <LongArrowUpSVG className="ms-2" />;
		if ((item?.price_net_money?.amount || 0) < (item?.last_price || 0))
			return <LongArrowDownSVG className="ms-2" />;
	};

	const searchTaxes = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getTaxSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const handleChangeLastPrice = async (item: PurchaseItemApi, index: number) => {
		if (!item.id) return;

		const lastPrice = await api.organization().getProductLastPrice(Number(item.product_id), item.volume);
		setValue(`items.${index}.last_price`, lastPrice?.amount);
	};

	const drawVolumeInput = (item: PurchaseItemApi, index: number) => {
		const volumes = item?.product?.product_volumes || [];
		const VOLUME_INPUT_INDEX = 2;

		if (volumes.length > 0) {
			const volumesOptions = volumes.map((volume) => ({
				id: volume.volume,
				label: `${volume.volume ?? ""} ${volume.label ?? ""}`,
			}));
			const defaultValueLabel = volumes.find(
				(volume) => Number(volume.volume) === Number(watch(`items.${index}.volume`))
			)?.label;
			return (
				<FormSelectGroup
					name={`items.${index}.volume`}
					errors={errors}
					openMenuOnFocus
					getOptionValue={(option) => option.id}
					defaultValue={{
						label: defaultValueLabel
							? `${watch(`items.${index}.volume`)} ${defaultValueLabel}`
							: watch(`items.${index}.volume`),
						id: watch(`items.${index}.volume`),
					}}
					onBlur={() => {
						setIndexToFocus(-1);
					}}
					tabIndex={index * INPUT_AMOUNT_USING_TAB_KEY + VOLUME_INPUT_INDEX}
					focus={indexToFocus === index}
					loadOptions={() => volumesOptions}
					control={control}
					data-testid={`items.${index}.volume`}
					forceCreateOption
					onCreateOption={(option) => setValue(`items.${index}.volume`, option)}
				/>
			);
		}

		return (
			<FormNumberInput
				onChange={(val) => {
					fetchProductLastPrice(item, index);
					recalculateSummaryTaxes(watchedItems, watch("tax_gross"), index, val);
				}}
				decimalScale={5}
				tabIndex={(index * INPUT_AMOUNT_USING_TAB_KEY + 2).toString()}
				errors={errors}
				name={`items.${index}.volume`}
				suffix={
					item?.product?.measure_type ? t(`enums.products.measure_types.${item?.product?.measure_type}`) : ""
				}
				control={control}
			/>
		);
	};

	const onChangeTotalPrice = async (
		value: number | undefined,
		item: PurchaseItemApi,
		index: number,
		isQuantity?: boolean
	) => {
		if (isQuantity) {
			setValue(`items.${index}.total_price_net_money`, {
				amount: value ? currency(value).multiply(item?.price_net_money?.amount || 0).value : 0,
				currency: watchedCurrency,
			});
		} else
			setValue(`items.${index}.total_price_net_money`, {
				amount: value ? currency(value).multiply(item?.quantity || 0).value : 0,
				currency: watchedCurrency,
			});
	};

	const onChangeTax = async (id: number, tax: TaxApi, index: number) => {
		registerObject(register, `items.${index}.tax`, ["amount", "name", "code", "id"]);
		setValue(`items.${index}.tax`, tax);
		setValue(`items.${index}.tax_id`, id, { shouldDirty: true });
		recalculateSummaryTaxes(watchedItems, watch("tax_gross"));
	};

	const onChangePrice = async (value: number | undefined, item: PurchaseItemApi, index: number) => {
		let newValue = 0;
		if (value && item.quantity) newValue = value / item.quantity;
		setValue(`items.${index}.price_net_money`, { amount: newValue, currency: watchedCurrency });
	};

	const handleCorrection = async (item: PurchaseItemApi, isProduct: boolean) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			if (isProduct) {
				await api.organization().correctProductRecipe(item.product_id);
			} else if (item.linked_product?.id) {
				await api.organization().correctIntegrationLinkRecipe(item.linked_product.id);
			}

			fetchPurchase && fetchPurchase();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<tbody className="purchase-form-items-table-body">
			{watchedItems.map((item, index) => (
				<React.Fragment key={fields[index].key}>
					<tr>
						<td className="action">
							<FormInput
								errors={errors}
								type="hidden"
								register={register}
								name={`items.${index}.position`}
								defaultValue={index}
							/>
							<FormInput
								type="hidden"
								errors={errors}
								register={register}
								name={`items.${index}.id`}
								defaultValue={item.id}
							/>
						</td>
						<td>
							{!item?.product?.id ? (
								<>
									<FormSelectGroup
										name={`items.${index}.external_product_name`}
										openMenuOnFocus
										errors={errors}
										onChange={(_, product) =>
											onChangeProduct(product, index, product && !product.id && product)
										}
										getOptionValue={(option) => onParseOptionValue(option)}
										getOptionLabel={(option) => {
											return option.product_type === "PACKAGE" ? (
												<PackageSearchSelectListItem label={option.label} />
											) : (
												option.label
											);
										}}
										defaultValue={{
											label: item?.external_product_name,
										}}
										forceCreateOption={
											(watch("status") === "UNLINKED" ||
												watch("status") === "DRAFT" ||
												!watch("status")) &&
											!watch("channel")?.id
										}
										tabIndex={index * INPUT_AMOUNT_USING_TAB_KEY + PRODUCT_INPUT_INDEX}
										loadOptions={searchProducts}
										control={control}
										data-testid={`items.${index}.external_product_name`}
									/>
									<FormInput
										type="hidden"
										errors={errors}
										register={register}
										name={`items.${index}.product_id`}
										defaultValue={item.product_id}
									/>
								</>
							) : !item?.external_product_id ? (
								<div className="d-flex align-items-center product-box">
									<DropdownSVG
										className="icon dropdown-icon"
										id={`icon-${index}`}
										style={{
											transform: isItemRowIndexShown(shownIndexes, index)
												? "rotate(180deg)"
												: "unset",
										}}
										onClick={() => showMore(shownIndexes, setShownIndexes, index)}
									/>
									<span className="ms-1">{`${index + 1}.`}</span>
									<Link
										rel={"noreferrer"}
										target={"_blank"}
										to={`/${organization.id}/products/${item.product.id}/edit`}
										className="mx-2"
									>
										{item.product.name}
										{getProductVolumeLabel(item.product, item)}
									</Link>
									<EditSVG
										className="me-2 icon edit-icon"
										onClick={() => setShowChangeProductModal(index)}
									/>
									<span>{`${t("common.word.own")}: `}</span>
									<span
										className={`${
											externalProductStates.length > 0 ? "mx-1" : "ms-1"
										} badge bg-${getInStockAmountBadgeColor(item?.product, productStates)}`}
									>
										{`${getStateForProduct(item?.product, productStates)?.in_stock_amount} ${t(
											`enums.products.measure_types.${item?.product?.measure_type}`
										)}`}
									</span>
									{externalProductStates.length > 0 && (
										<>
											<span>{getExternalStateName()}</span>
											<span
												className={`ms-1 badge bg-${getInStockAmountBadgeColor(
													item?.product,
													externalProductStates
												)}`}
											>
												{`${
													getStateForProduct(item?.product, externalProductStates)
														?.in_stock_amount
												} ${t(`enums.products.measure_types.${item?.product?.measure_type}`)}`}
											</span>
										</>
									)}
									{showDeviationFromReferencePrice && drawPriceDeviation(item)}
									{!showDeviationFromReferencePrice && (
										<DocumentItemPriceDifferential
											itemPrice={item.price_net_money}
											lastPrice={
												item.last_price !== undefined
													? { amount: item.last_price, currency: "" }
													: undefined
											}
										/>
									)}
								</div>
							) : (
								<>
									<span className="me-1">{`${index + 1}.`}</span>
									<Link
										rel="noreferrer"
										target="_blank"
										to={`/${organization.id}/products/${item.product.id}/edit`}
									>
										{item.product.name}
										{getProductVolumeLabel(item.product, item)}
									</Link>
								</>
							)}
						</td>
						<td>{drawVolumeInput(item, index)}</td>
						<td>
							<FormNumberInput
								errors={errors}
								name={`items.${index}.quantity`}
								suffix=""
								onChange={(val) => {
									onChangeTotalPrice(val, item, index, true);
									recalculateSummaryTaxes(
										watchedItems,
										watch("tax_gross"),
										index,
										parseFloat(`${val}`),
										"quantity"
									);
								}}
								tabIndex={(index * INPUT_AMOUNT_USING_TAB_KEY + QUANTITY_INPUT_INDEX).toString()}
								defaultValue={Number(item.quantity)}
								decimalScale={5}
								control={control}
							/>
						</td>
						<td className="nowrap">{getTotalVolume(item)}</td>
						<td>
							<FormSelectGroup
								name={`items.${index}.tax_id`}
								errors={errors}
								onChange={(id, tax) => onChangeTax(id, tax, index)}
								getOptionLabel={(option) => option.label}
								isClearable={false}
								getOptionValue={(option) => option.id}
								defaultValue={{
									id: item?.tax?.id,
									label: item?.tax?.name,
								}}
								loadOptions={searchTaxes}
								control={control}
								data-testid={`items.${index}.tax_id`}
							/>
						</td>
						<td>
							<FormMoneyInput
								errors={errors}
								name={`items.${index}.price_net_money.amount`}
								withoutCurrency
								onChange={(val) => {
									onChangeTotalPrice(val, item, index);
									recalculateSummaryTaxes(watchedItems, watch("tax_gross"), index, val);
									handleChangeLastPrice(item, index);
								}}
								tabIndex={(index * INPUT_AMOUNT_USING_TAB_KEY + PRICE_NET_INPUT_INDEX).toString()}
								control={control}
								defaultValue={
									getValues("tax_gross")
										? item?.price_gross_money?.amount
										: item?.price_net_money?.amount
								}
								currency={""}
							/>
						</td>
						<td colSpan={fields.length > 1 ? 1 : 2}>
							<FormMoneyInput
								errors={errors}
								name={`items.${index}.total_price_net_money.amount`}
								withoutCurrency
								onChange={(val) => {
									onChangePrice(val, item, index);
									recalculateSummaryTaxes(
										watchedItems,
										watch("tax_gross"),
										index,
										val,
										undefined,
										true
									);
									handleChangeLastPrice(item, index);
								}}
								defaultValue={
									getValues("tax_gross")
										? item?.total_price_gross_money?.amount
										: item?.total_price_net_money?.amount
								}
								control={control}
								currency={""}
							/>
						</td>
						{fields.length > 1 && (
							<td
								className={`${
									!((fields.length === 1 && index === 0) || index === fields.length - 1)
										? "actions"
										: "action"
								}`}
							>
								<div className="d-flex align-items-center gap-2">
									{!((fields.length === 1 && index === 0) || index === fields.length - 1) && (
										<RemoveSVG className="icon" onClick={() => onRemove(index)} />
									)}
									{(item?.product_id || item?.linked_product?.id) && (
										<Dropdown className="icon">
											<Dropdown.Toggle as={MoreSVG} />
											<Dropdown.Menu>
												{item?.product_id && (
													<Dropdown.Item onClick={() => handleCorrection(item, true)}>
														{t("common.action.recipe_correction")}
													</Dropdown.Item>
												)}
												{item?.linked_product && item.linked_product?.id && (
													<Dropdown.Item onClick={() => handleCorrection(item, false)}>
														{t("common.action.integration_link_correction")}
													</Dropdown.Item>
												)}
											</Dropdown.Menu>
										</Dropdown>
									)}
								</div>
							</td>
						)}
					</tr>
					{isItemRowIndexShown(shownIndexes, index) && getStateForProduct(item?.product, productStates) && (
						<tr>
							<td className="action" />
							<td>
								<div className="d-flex flex-column">
									<div className="discounted-checkbox-container">
										<FormCheck
											name={`items.${index}.discounted`}
											label={t("modules.purchase.field.discounted.title")}
											errors={errors}
											register={register}
										/>
									</div>
									{item?.product?.type !== "COMPOSITE" && (
										<small className="text-muted font-weight-bold">
											{`${t("common.word.last_price")}: ${FormatMoney(
												item?.product?.last_price_money
											)}`}
										</small>
									)}
									<small className="text-muted font-weight-bold">
										{`${t("common.word.cost")}: ${FormatMoney(item?.product?.cost_price_money)}`}
									</small>
									<small className="text-muted font-weight-bold">
										{drawProductState(item.product, productStates, t)}
									</small>
									<small className="text-muted font-weight-bold">
										{`${t("common.word.default_volume")}: ${item?.product?.default_volume || "-"}`}
									</small>
								</div>
							</td>
							<td colSpan={7}>
								<FormInput
									errors={errors}
									register={register}
									className="comment-input"
									name={`items.${index}.comment`}
									defaultValue={item?.comment}
									placeholder={t("common.word.comment.placeholder")}
								/>
							</td>
						</tr>
					)}
				</React.Fragment>
			))}
		</tbody>
	);
};

export default PurchaseFormItemsTableBody;
